import React, { useEffect, useState } from "react";
import { Box, Container, Divider, Stack, Typography, CircularProgress } from "@mui/material";
import { MuiButton, MuiSelector } from "@ace/ace-ui-web";
import SettingsIcon from "@mui/icons-material/Settings";
import { toast } from "sonner";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import {
    fetchCutoffReminder,
    submitCutoffReminder,
} from "../../redux/GigShiftSettingsReducer/gigShiftSettingsSlice";

interface ReminderSettings {
    cutoffDay: string; // "0" (Sun) - "6" (Sat)
    time: string;      // "HH:mm" 24h from <input type="time">
}

const DEFAULTS: ReminderSettings = {
    cutoffDay: "1", // Monday
    time: "14:30",
};

const DAY_OPTIONS = [
    { label: "Sunday",    value: "0" },
    { label: "Monday",    value: "1" },
    { label: "Tuesday",   value: "2" },
    { label: "Wednesday", value: "3" },
    { label: "Thursday",  value: "4" },
    { label: "Friday",    value: "5" },
    { label: "Saturday",  value: "6" },
];

const DAY_LABELS: Record<number, string> =
    DAY_OPTIONS.reduce((acc, o) => ({ ...acc, [Number(o.value)]: o.label }), {});

/** "HH:mm" -> "HH:mm:ss" for C# TimeOnly. */
const toTimeOnlyString = (hhmm: string): string => {
    if (!hhmm) return "";
    return hhmm.length === 5 ? `${hhmm}:00` : hhmm;
};

/** "HH:mm:ss" (or "HH:mm") -> "HH:mm" for <input type="time">. Purely string ops. */
const toInputTime = (hhmmss: string | null | undefined): string => {
    if (!hhmmss) return "";
    const m = /^(\d{2}):(\d{2})(?::\d{2})?$/.exec(hhmmss.trim());
    return m ? `${m[1]}:${m[2]}` : "";
};

/**
 * Format a stored "HH:mm:ss" for user-facing display as 12-hour "h:mm AM/PM".
 * Pure string parsing — no Date, no timezone conversion. The time is a site
 * wall-clock policy and must render identically regardless of the viewer's zone.
 */
const formatWallClockTime = (hhmmss: string): string => {
    const m = /^(\d{2}):(\d{2})(?::\d{2})?$/.exec(hhmmss.trim());
    if (!m) return hhmmss;
    const h24 = Number(m[1]);
    const min = m[2];
    const period = h24 >= 12 ? "PM" : "AM";
    const h12 = ((h24 + 11) % 12) + 1;
    return `${h12}:${min} ${period}`;
};

const isValidTime = (t: string): boolean =>
    /^([01]\d|2[0-3]):[0-5]\d:[0-5]\d$/.test(t);

const getCurrentDayLabel = (cutoffDay?: number | null): string | null => {
    if (cutoffDay == null) return null;
    if (cutoffDay >= 0 && cutoffDay <= 6) return DAY_LABELS[cutoffDay];
    return `Day ${cutoffDay}`;
};

const SettingsPage: React.FC = () => {
    const dispatch = useAppDispatch();
    const { user: currentUser } = useAppSelector((state) => state.currentUser);
    const { saving, loading, current, fetchError } = useAppSelector((state) => state.gigShiftSettings);

    const [settings, setSettings] = useState<ReminderSettings>(DEFAULTS);

    // Fetch the site's current cutoff on mount and whenever the site changes.
    useEffect(() => {
        const siteCd = currentUser?.siteCd?.trim();
        if (siteCd) {
            dispatch(fetchCutoffReminder(siteCd));
        }
    }, [dispatch, currentUser?.siteCd]);

    // Prefill the form when the API returns a stored cutoff.
    useEffect(() => {
        if (!current) return;
        const day = Number(current.cutoffDay);
        setSettings({
            cutoffDay: day >= 0 && day <= 6 ? String(day) : DEFAULTS.cutoffDay,
            time: toInputTime(current.cutoffTime) || DEFAULTS.time,
        });
    }, [current]);

    // Surface real errors (404 is swallowed in the slice as empty-state).
    useEffect(() => {
        if (fetchError) {
            toast.error(`Failed to load cutoff reminder: ${fetchError}`);
        }
    }, [fetchError]);

    const handleSave = async () => {
        const siteCd = currentUser?.siteCd?.trim() ?? "";
        const cutoffDay = parseInt(settings.cutoffDay, 10);
        const cutoffTime = toTimeOnlyString(settings.time);

        if (!siteCd) {
            toast.error("No site code found on the current user. Please re-login and try again.");
            return;
        }
        if (!Number.isInteger(cutoffDay) || cutoffDay < 0 || cutoffDay > 6) {
            toast.error("Please select a valid day of the week.");
            return;
        }
        if (!isValidTime(cutoffTime)) {
            toast.error("Please select a valid time.");
            return;
        }

        try {
            const result = await dispatch(
                submitCutoffReminder({ siteCd, cutoffDay, cutoffTime })
            ).unwrap();
            toast.success(result?.message || "Cutoff reminder saved successfully.");
        } catch (err) {
            console.error("Failed to save cutoff reminder:", err);
            toast.error(
                typeof err === "string"
                    ? err
                    : "Something went wrong while saving. Please try again."
            );
        }
    };

    const handleReset = () => setSettings(DEFAULTS);

    // Values used for the read-only "current" summary.
    const currentDayLabel = getCurrentDayLabel(current?.cutoffDay);

    return (
        <Container
            maxWidth={false}
            sx={{
                py: { xs: 2.5, md: 3, lg: 3.5, xl: 4 },
                px: { xs: 1, sm: 2, md: 3.5, lg: 4, xl: 5 },
            }}
        >
            <Stack spacing={2.5}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                    <SettingsIcon fontSize="large" />
                    <Stack spacing={0.5}>
                        <Typography variant="h3" component="h1" className="page-title">
                            Settings
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Configure when gig worker request reminders are sent
                            {currentUser?.siteCd ? ` for site ${currentUser.siteCd}` : ""}.
                        </Typography>
                    </Stack>
                </Stack>

                <Divider />

                <Stack spacing={2} sx={{ maxWidth: 560 }}>
                    <Typography variant="h6">Gig Cutoff Reminder</Typography>

                    {/* Current stored value */}
                    <Box
                        sx={{
                            p: 1.5,
                            borderRadius: "calc(var(--radius) - 0.125rem)",
                            border: "1px solid var(--color-border)",
                            backgroundColor: "#fafafa",
                        }}
                    >
                        {(() => {
                            if (loading) {
                                return (
                                    <Stack direction="row" alignItems="center" spacing={1}>
                                        <CircularProgress size={14} />
                                        <Typography variant="body2" color="text.secondary">
                                            Loading current cutoff…
                                        </Typography>
                                    </Stack>
                                );
                            }
                            if (current) {
                                return (
                                    <Stack spacing={0.25}>
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                            Current cutoff for {current.siteCd}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {currentDayLabel} at {formatWallClockTime(current.cutoffTime)}
                                            {" "}<em>(site local time)</em>
                                        </Typography>
                                    </Stack>
                                );
                            }
                            return (
                                <Typography variant="body2" color="text.secondary">
                                    No cutoff reminder has been configured for this site yet.
                                </Typography>
                            );
                        })()}
                    </Box>

                    <Stack spacing={0.5}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>Day of Week</Typography>
                        <Box sx={{ maxWidth: 280 }}>
                            <MuiSelector
                                value={settings.cutoffDay}
                                options={DAY_OPTIONS}
                                onChange={(val: string) => setSettings((prev) => ({ ...prev, cutoffDay: val }))}
                                placeholder="Select a day"
                            />
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                            Reminders will be sent on this day each week.
                        </Typography>
                    </Stack>

                    <Stack spacing={0.5}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>Time</Typography>
                        <input
                            type="time"
                            value={settings.time}
                            onChange={(e) => setSettings((prev) => ({ ...prev, time: e.target.value }))}
                            style={{
                                padding: "0.5rem 0.75rem",
                                border: "1px solid var(--color-border)",
                                borderRadius: "calc(var(--radius) - 0.125rem)",
                                fontSize: "0.875rem",
                                maxWidth: 200,
                            }}
                        />
                        <Typography variant="caption" color="text.secondary">
                            To give gig workers ample time to submit their shift requests for the following week, notifications are sent 2 hours prior to this cutoff time weekly (local timezone).
                        </Typography>
                    </Stack>

                    <Box sx={{ display: "flex", gap: 1.5, pt: 1, alignItems: "center" }}>
                        <MuiButton onClick={handleSave} disabled={saving || loading}>
                            {saving ? (
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                    <CircularProgress size={16} color="inherit" />
                                    Saving...
                                </Box>
                            ) : (
                                "Save Changes"
                            )}
                        </MuiButton>
                        <MuiButton variant="outlined" onClick={handleReset} disabled={saving || loading}>
                            Reset to Defaults
                        </MuiButton>
                    </Box>
                </Stack>
            </Stack>
        </Container>
    );
};

export default SettingsPage;